/**
 * Saved views.
 *
 * A triage list is never "all users" — it is "vendors in DOCUMENTS_SUBMITTED,
 * oldest first" or "unpaid orders older than a week". Rebuilding that from four
 * dropdowns every morning is the actual daily cost of a thin filter bar.
 *
 * A view is just the query string. That falls out of filters already living in
 * the URL: saving is `location.search`, applying is a navigate, and every view
 * is therefore also a shareable link. Nothing here needs the backend.
 */
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bookmark, BookmarkPlus, Check, Star, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { notify } from '@/components/ui/toaster';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export type SavedView = {
  id: string;
  name: string;
  /** Query string, without the leading '?'. */
  query: string;
  /** Pinned views also render as one-click chips above the table. */
  pinned: boolean;
};

const KEY_PREFIX = 'pm-admin-views:';
const MAX_VIEWS = 20;

function read(module: string): SavedView[] {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + module);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as SavedView[]) : [];
  } catch {
    return [];
  }
}

function write(module: string, views: SavedView[]): void {
  try {
    localStorage.setItem(KEY_PREFIX + module, JSON.stringify(views));
  } catch {
    /* quota / private mode — views just don't persist */
  }
}

export function use_saved_views(module: string) {
  const [views, set_views] = useState<SavedView[]>(() => read(module));

  // Re-read when the module changes so navigating between lists doesn't show
  // the previous list's views for a frame.
  useEffect(() => {
    set_views(read(module));
  }, [module]);

  const persist = useCallback(
    (next: SavedView[]) => {
      set_views(next);
      write(module, next);
    },
    [module],
  );

  const save = useCallback(
    (name: string, query: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      const current = read(module);
      // Same name overwrites rather than accumulating "Backlog", "Backlog"…
      const without = current.filter((v) => v.name.toLowerCase() !== trimmed.toLowerCase());
      const view: SavedView = {
        id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
        name: trimmed,
        query,
        pinned: false,
      };
      persist([view, ...without].slice(0, MAX_VIEWS));
    },
    [module, persist],
  );

  const remove = useCallback(
    (id: string) => persist(read(module).filter((v) => v.id !== id)),
    [module, persist],
  );

  const toggle_pin = useCallback(
    (id: string) =>
      persist(read(module).map((v) => (v.id === id ? { ...v, pinned: !v.pinned } : v))),
    [module, persist],
  );

  return { views, save, remove, toggle_pin };
}

/**
 * A secondary control living inside a menu row.
 *
 * Radix resolves an item selection on pointerdown/pointerup, so a nested
 * control has to stop both phases or activating it also fires the row's own
 * action — deleting a view would delete it AND navigate to it.
 */
function RowAction({
  label,
  test_id,
  on_activate,
  danger,
  children,
}: {
  label: string;
  test_id: string;
  on_activate: () => void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  const swallow = (e: React.PointerEvent | React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };
  return (
    <button
      type="button"
      aria-label={label}
      data-testid={test_id}
      onPointerDown={swallow}
      onPointerUp={swallow}
      onClick={(e) => {
        swallow(e);
        on_activate();
      }}
      className={cn(
        'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors',
        danger
          ? 'text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
          : 'hover:bg-muted',
      )}
    >
      {children}
    </button>
  );
}

/**
 * The menu, plus the pinned-view chips.
 *
 * `is_filtered` gates saving: a view of "no filters at all" is the default
 * list, and offering to save it invites a menu full of identical entries.
 */
export function SavedViews({ module, is_filtered }: { module: string; is_filtered: boolean }) {
  const { views, save, remove, toggle_pin } = use_saved_views(module);
  const location = useLocation();
  const navigate = useNavigate();
  const current_query = location.search.replace(/^\?/, '');
  const [naming, set_naming] = useState(false);
  const [name, set_name] = useState('');

  const pinned = views.filter((v) => v.pinned);
  const active = views.find((v) => v.query === current_query);

  function apply(view: SavedView) {
    navigate({ pathname: location.pathname, search: view.query ? `?${view.query}` : '' });
  }

  function commit_save() {
    const trimmed = name.trim();
    if (!trimmed) return;
    save(trimmed, current_query);
    set_name('');
    set_naming(false);
    notify.success(`Saved “${trimmed}”`, {
      description: 'Pin it from the Views menu to keep it one click away.',
    });
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {pinned.map((v) => (
        <button
          key={v.id}
          type="button"
          onClick={() => apply(v)}
          className={cn(
            'inline-flex h-7 items-center gap-1.5 rounded-pill px-2.5 text-data font-semibold transition-colors',
            v.query === current_query
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary',
          )}
          data-testid={`pinned-view-${v.id}`}
        >
          <Star className="h-3 w-3" />
          {v.name}
        </button>
      ))}

      <DropdownMenu>
        <DropdownMenuTrigger
          className={cn(
            'inline-flex h-7 items-center gap-1.5 rounded-pill px-2.5 text-data font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            active ? 'bg-primary/12 text-primary' : 'text-muted-foreground hover:bg-muted',
          )}
          data-testid="views-menu"
        >
          <Bookmark className="h-3.5 w-3.5" />
          {active ? active.name : 'Views'}
          {views.length > 0 && !active && (
            <Badge tone="neutral" variant="outline" size="sm">
              {views.length}
            </Badge>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[15rem]">
          <DropdownMenuLabel>Saved views</DropdownMenuLabel>
          {views.length === 0 ? (
            <p className="px-2 py-2 text-data text-muted-foreground">
              Filter this list, then save it as a view.
            </p>
          ) : (
            views.map((v) => (
              <DropdownMenuItem
                key={v.id}
                onSelect={() => apply(v)}
                data-testid={`view-${v.id}`}
                className="pr-1"
              >
                <span className="min-w-0 flex-1 truncate">{v.name}</span>
                {v.query === current_query && <Check className="h-4 w-4 text-primary" />}
                {/*
                  Pin and delete sit inside the row whose primary action is
                  "apply". Radix decides an item was selected on POINTER
                  events, not click, so stopping `onClick` alone let a delete
                  also navigate. Both pointer phases are stopped, and the
                  action runs from the button's own click.
                */}
                <RowAction
                  label={v.pinned ? `Unpin ${v.name}` : `Pin ${v.name}`}
                  test_id={`view-pin-${v.id}`}
                  on_activate={() => toggle_pin(v.id)}
                >
                  <Star className={cn('h-3.5 w-3.5', v.pinned && 'fill-primary text-primary')} />
                </RowAction>
                <RowAction
                  label={`Delete ${v.name}`}
                  test_id={`view-delete-${v.id}`}
                  on_activate={() => remove(v.id)}
                  danger
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </RowAction>
              </DropdownMenuItem>
            ))
          )}
          {is_filtered && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                // Deliberately NOT preventDefault: keeping the menu open leaves
                // Radix's modal guard in place, which sets `pointer-events:
                // none` on the body — the naming input rendered outside the
                // menu was then completely inert. Let the menu close; the input
                // takes focus itself.
                onSelect={() => set_naming(true)}
                data-testid="view-save"
              >
                <BookmarkPlus className="h-4 w-4" />
                Save current filters
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {naming && (
        <span className="inline-flex items-center gap-1">
          <input
            autoFocus
            className="console-field !h-7 w-40 !text-data"
            placeholder="Name this view"
            value={name}
            onChange={(e) => set_name(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit_save();
              if (e.key === 'Escape') {
                set_naming(false);
                set_name('');
              }
            }}
            aria-label="View name"
            data-testid="view-name-input"
          />
          <button
            type="button"
            onClick={commit_save}
            disabled={!name.trim()}
            className="inline-flex h-7 items-center rounded-pill bg-primary px-2.5 text-data font-semibold text-primary-foreground disabled:opacity-40"
            data-testid="view-name-save"
          >
            Save
          </button>
        </span>
      )}
    </div>
  );
}
